import React from 'react';
import { Loader2, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ModuleStatusViewProps {
    moduleId: string;
    moduleLabel: string;
    description: string;
}

const ModuleStatusView: React.FC<ModuleStatusViewProps> = ({ moduleId, moduleLabel, description }) => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-slate-50 p-4 md:p-8 animate-in fade-in duration-500">
            <div className="max-w-2xl mx-auto space-y-8">
                <button
                    onClick={() => navigate('/modules')}
                    className="flex items-center gap-2 text-slate-400 hover:text-slate-600 transition-colors text-xs font-black uppercase tracking-widest"
                >
                    <ArrowLeft size={14} />
                    Back to Modules
                </button>

                <div className="text-center space-y-4">
                    <div className="inline-flex p-6 bg-white rounded-3xl shadow-sm border border-slate-100 text-indigo-600">
                        <Loader2 size={48} className="animate-spin" />
                    </div>
                    <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">
                        {moduleLabel}
                    </h1>
                    <p className="text-slate-500 font-medium max-w-md mx-auto">
                        {description}
                    </p>
                </div>

                <div className="bg-white rounded-[2.5rem] p-8 border border-slate-200 shadow-sm space-y-6">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                                <SovereignIcon />
                            </div>
                            <span className="text-sm font-bold text-slate-700 uppercase tracking-tight">System Status</span>
                        </div>
                        <span className="flex items-center gap-2 text-xs font-black text-emerald-600 uppercase tracking-widest">
                            <CheckCircle2 size={14} />
                            Online
                        </span>
                    </div>

                    <div className="space-y-4">
                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest pl-2">
                            Deployment Pipeline
                        </h3>
                        <div className="space-y-3">
                            <PipelineStep label="Agent Orchestration" status="completed" />
                            <PipelineStep label="Dependency Resolution" status="completed" />
                            <PipelineStep label="Infrastructure Provisioning" status="running" />
                            <PipelineStep label="Asset Generation" status="pending" />
                        </div>
                    </div>
                </div>

                <div className="bg-amber-50 border border-amber-100 p-6 rounded-3xl flex gap-4 items-start">
                    <AlertCircle className="text-amber-500 shrink-0" size={20} />
                    <p className="text-sm text-amber-800 font-medium">
                        This module is currently executing a Sovereign workflow. Please wait while the agents synchronize your data and provision assets.
                    </p>
                </div>
            </div>
        </div>
    );
};

const PipelineStep = ({ label, status }: { label: string; status: 'completed' | 'running' | 'pending' }) => (
    <div className="flex items-center justify-between p-4 rounded-2xl bg-white border border-slate-100 group hover:border-indigo-200 transition-all">
        <span className={`text-sm font-bold ${status === 'completed' ? 'text-slate-700' : status === 'running' ? 'text-indigo-600' : 'text-slate-400'}`}>
            {label}
        </span>
        {status === 'completed' && <CheckCircle2 size={18} className="text-emerald-500" />}
        {status === 'running' && <Loader2 size={18} className="text-indigo-600 animate-spin" />}
        {status === 'pending' && <div className="w-4 h-4 rounded-full bg-slate-100 border border-slate-200" />}
    </div>
);

const SovereignIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    </svg>
);

export default ModuleStatusView;
